import { computed, inject, Ref, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth.store";
import AuthRepository from "@/repositories/AuthRepository";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";

export default class LoginViewModel {
  public name = ref<string>("");
  public password = ref<string>("");
  private realName = ""; // Almacena el valor real del nombre

  public nameRules: Array<(value: string) => boolean | string>;
  public passwordRules: Array<(value: string) => boolean | string>;

  public visible = ref<boolean>(false);

  private toast: Toast;
  private authStore = useAuthStore();
  private router = useRouter();

  public appName = process.env.VUE_APP_APP_NAME || "";
  public whatsapp = process.env.VUE_APP_WHATSAPP || "";
  public webPage = process.env.VUE_APP_WEBPAGE || "";
  logoImage: string;

  public rememberMe = computed({
    get: () => this.authStore.getRememberMe,
    set: (value: boolean) => {
      if (value != undefined) {
        this.authStore.updateRememberMe({ rememberme: value });
      }
    },
  });

  public loginForm: Ref<null | {
    validate: () => Promise<{ valid: boolean }>;
    reset: () => void;
    resetValidation: () => void;
  }>;

  constructor(
    loginForm: Ref<null | {
      validate: () => Promise<{ valid: boolean }>;
      reset: () => void;
      resetValidation: () => void;
    }>
  ) {
    this.loginForm = loginForm;

    this.logoImage = require("@/assets/images/logo.png");
    this.toast = inject("toast") as Toast;

    this.nameRules = [
      (v) => !!v || "El documento es obligatorio",
      (v) =>
        /^[\d*]+$/.test(v) || "Solo se permiten números o caracteres ocultos.",
      (v) =>
        this.getRealName().length >= 8 ||
        "El documento debe tener al menos 8 caracteres reales",
    ];

    this.passwordRules = [
      (v) => !!v || "La contraseña es obligatoria",
      (v) => v.length >= 6 || "La contraseña debe tener al menos 6 caracteres",
    ];

    this.initializeLogin();
  }

  private async initializeLogin(): Promise<void> {
    const rememberMe = await this.authStore.getRememberMe; // Acceder directamente al store
    const storedLogin = await this.authStore.getLogin;

    if (rememberMe && storedLogin) {
      this.realName = storedLogin;
      this.name.value = this.maskDigits(storedLogin);
    } else {
      this.realName = "";
      this.name.value = "";
    }
  }

  private maskDigits(value: string): string {
    if (value.length >= 5) {
      return "*****" + value.slice(5);
    }
    return value;
  }

  private getRealName(): string {
    return this.name.value;
  }

  public handleBackPress = (): void => {
    if (this.rememberMe.value && this.name.value.includes("*")) {
      this.realName = ""; // Limpiar el valor real
      this.name.value = ""; // Limpiar el campo visible
    }
  };

  public handleNameInput(): void {
    if (!this.name.value.includes("*")) {
      this.realName = this.getRealName();
    }
  }

  login = async (): Promise<void> => {
    if (!this.loginForm.value) return;

    const { valid } = await this.loginForm.value.validate();
    if (!valid) return;

    try {
      const loginName = this.name.value.includes("*")
        ? this.realName
        : this.name.value;

      const response = await AuthRepository.login(
        loginName,
        this.password.value
      );

      this.authStore.authenticateUser({
        login: loginName,
        password: this.password.value,
        rememberme: this.rememberMe.value,
        data: response.data.data,
      });

      this.toast.showToast("Bienvenido", ToastType.Success);
      this.router.push("/app/home");
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Error de servidor.";
      console.error("Error during login:", error.response?.data || error);
      this.toast.showToast(errorMessage, ToastType.Error);
    }
  };

  resetForm = (): void => {
    if (this.loginForm.value) {
      this.loginForm.value.reset();
      this.loginForm.value.resetValidation();
    }
    this.name.value = "";
    this.password.value = "";
    this.realName = "";
    this.rememberMe.value = false;
  };

  goToWeb(): void {
    if (this.webPage) {
      window.open(this.webPage, "_blank");
    }
  }

  goToWhatsApp(): void {
    if (this.whatsapp) {
      const message = encodeURIComponent(
        "¡Hola! Me gustaría obtener más información sobre sus servicios."
      );
      window.open(`https://wa.me/${this.whatsapp}?text=${message}`, "_blank");
    }
  }
}
